import { inject, Injectable } from '@angular/core';
import { QuoteToolLiteStore, SharedDataStore } from '../../store';
import { QuoteToolLiteModeService } from '../modes/quote-tool-lite-mode.service';
import { PersistentStoreService } from '../persistent-store.service';
import { DeeplinkTrackingService } from '../modes/strategies';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from '@lx/shared/logger';

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly quoteToolLiteModeService = inject(QuoteToolLiteModeService);
  private readonly qtLiteStore = inject(QuoteToolLiteStore);
  private readonly persistentStateService = inject(PersistentStoreService);
  private readonly deeplinkTrackingService = inject(DeeplinkTrackingService);
  protected readonly logger = inject(LoggerService);

  /**
   * Initialize the app by loading the shared data, the data from quote lite mode service (for default access or
   * for deep links) and with all that information, initialize the QTLite store and the persistent state service.
   *
   * This is done to make sure that all the data is loaded before the application starts.
   * After everything is initialized, track the deeplink_opened GA event if deeplinkType exists in the store.
   */
  initialize(): Promise<boolean> {
    return new Promise((resolve: (value: boolean) => void, reject: (reason: string) => void) => {
      this.sharedDataStore
        .loadSharedData()
        .pipe(
          switchMap((loaded) => {
            if (!loaded) {
              return of(false);
            }

            return this.quoteToolLiteModeService.loadData$();
          }),
        )
        .subscribe((loaded) => {
          if (!loaded) {
            const errorMessage = 'Error loading data from quote tool lite mode service';
            this.logger.error(errorMessage);
            reject(errorMessage);
            return;
          }
          // Load the initial state of the quote tool lite store
          this.qtLiteStore.initializeStore();
          this.persistentStateService.initializePersistingState();

          this.deeplinkTrackingService.trackDeeplinkOpenedIfPresent();

          resolve(true);
        });
    });
  }
}
